/**
 * 老客户附件：独立档案也可保存检测单、照片及授权材料，无需伪造工程项目。
 * 所有读写均通过客户鉴权接口，不能使用通用文件直链绕过分公司范围。
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download, Loader2, Upload } from "lucide-react";
import api from "../../lib/api";
import { fmtDT } from "../../lib/date";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { errMsg } from "../referral/refUtils";

const categories = { inspection: ["检测单", "Inspection"], photo: ["照片", "Photo"], authorization: ["授权资料", "Authorization"], other: ["其他", "Other"] };

/** 附件列表与客户详情分别加载，附件错误不应遮住客户基本信息。 */
export default function PastCustomerFiles({ customerId, contacts = [], canUpload, zh }) {
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("other");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const input = useRef(null);
  const base = `/aftersales/customers/${encodeURIComponent(customerId)}/files`;
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(base); setItems(data.items || []); setError(""); }
    catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, [base]);
  useEffect(() => { load(); }, [load]);
  const upload = async (event) => {
    event.preventDefault();
    if (!file || busy) return;
    if (file.size > 20 * 1024 * 1024) { setError(zh ? "文件不能超过 20 MB" : "File must be 20 MB or less"); return; }
    const form = new FormData(); form.append("file", file); form.append("category", category);
    setBusy(true); setError("");
    try {
      await api.post(base, form);
      setFile(null); if (input.current) input.current.value = "";
      await load();
    } catch (e) { setError(errMsg(e)); }
    finally { setBusy(false); }
  };
  const download = async (item) => {
    setError("");
    try {
      const { data } = await api.get(`${base}/${encodeURIComponent(item.id)}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a"); link.href = url; link.download = item.filename; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(errMsg(e)); }
  };
  return <section className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="asc-files">
    <h2 className="font-semibold">{zh ? "客户附件" : "Customer files"}</h2>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {loading ? <p className="text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 mr-1 animate-spin" />{zh ? "加载附件" : "Loading files"}</p> : items.length ?
      <ul className="divide-y divide-border">{items.map((item) => {
        const contact = item.contactId && contacts.find((row) => row.id === item.contactId);
        return <li key={item.id} className="flex items-center gap-3 py-2"><div className="min-w-0 flex-1"><p className="break-all text-sm">{item.filename}</p><p className="text-xs text-muted-foreground">{(categories[item.category] || categories.other)[zh ? 0 : 1]}{item.contactId ? ` · ${zh ? "回访附件" : "Follow-up file"}${contact?.at ? ` ${fmtDT(contact.at, "—")}` : ""}` : ""} · {fmtDT(item.createdAt, "—")} · {item.createdByName || item.createdBy}</p></div><Button type="button" variant="outline" size="sm" onClick={() => download(item)}><Download className="h-4 w-4 mr-1" />{zh ? "下载" : "Download"}</Button></li>;
      })}</ul> :
      <p className="text-sm text-muted-foreground">{zh ? "暂无附件" : "No files yet"}</p>}
    {canUpload && <form onSubmit={upload} className="space-y-3 rounded-lg border border-dashed border-border p-3" onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); if (!busy && event.dataTransfer.files.length) setFile(event.dataTransfer.files[0]); }}>
      <p className="text-xs text-muted-foreground">{zh ? "选择或拖入 PDF、图片或 Word 文件，单个不超过 20 MB。" : "Choose or drop a PDF, image or Word file up to 20 MB."}</p>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_12rem]">
        <div className="space-y-1.5"><Label htmlFor="asc-file-input">{zh ? "选择文件" : "Choose file"}</Label><Input ref={input} id="asc-file-input" data-testid="asc-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.doc,.docx" disabled={busy} onChange={(event) => setFile(event.target.files?.[0] || null)} /></div>
        <div className="space-y-1.5"><Label htmlFor="asc-file-category">{zh ? "分类" : "Category"}</Label><Select value={category} disabled={busy} onValueChange={setCategory}><SelectTrigger id="asc-file-category"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(categories).map(([key, labels]) => <SelectItem key={key} value={key}>{labels[zh ? 0 : 1]}</SelectItem>)}</SelectContent></Select></div>
      </div>
      {file && <p className="text-xs text-muted-foreground break-all">{file.name}</p>}
      <Button type="submit" size="sm" disabled={!file || busy} data-testid="asc-file-upload">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{zh ? "上传附件" : "Upload"}</Button>
    </form>}
  </section>;
}
